import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '@/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { dateFormat } from '@/constants';
import { TestIds } from '@/shared/test-ids';

type RepeatSlotModalProps = {
  startDate: Date;
  onClose: () => void;
  onAdd: (endDate: Date) => void;
};

export const RepeatSlotModal = ({ startDate, onClose, onAdd }: RepeatSlotModalProps) => {
  const [t] = useTranslation('common');

  const [repeatEndDate, setRepeatEndDate] = useState<Date | null>(() => {
    const tomorrow = new Date(startDate);
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  });

  const minEndDate = useMemo(() => {
    const nextDayAfterStart = new Date(startDate);
    nextDayAfterStart.setHours(0, 0, 0, 0);
    nextDayAfterStart.setDate(nextDayAfterStart.getDate() + 1);

    return nextDayAfterStart;
  }, [startDate]);

  return (
    <div
      data-testid={TestIds.digitalSignage.repeatSlotModal}
      className='absolute left-1/2 top-1/2 z-50 h-auto w-[350px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-4 shadow-[0_8px_16px_0_#2849867A]'>
      <Label text={t('repeatUntil')} type={LABEL_TYPE.H3} className='!text-primary-blue' />
      <Label
        text={t('repeatSlotHint')}
        type={LABEL_TYPE.PARAGRAPH2}
        className='mt-2 !text-secondary-gray'
      />

      <div className='mt-4 flex w-full flex-row gap-x-2'>
        <DateInput
          id='repeat-start-date'
          label={t('startDate')}
          selected={startDate}
          onChange={() => undefined}
          isRequired={false}
          dateFormat={dateFormat}
          disabled
        />
        <DateInput
          id='repeat-end-date'
          label={t('endDate')}
          selected={repeatEndDate}
          onChange={(date: Date) => {
            const normalizedDate = new Date(date);
            normalizedDate.setHours(0, 0, 0, 0);
            setRepeatEndDate(normalizedDate);
          }}
          isRequired={false}
          dateFormat={dateFormat}
          minDate={minEndDate}
        />
      </div>

      <div className='mt-6 flex w-full flex-row gap-x-2'>
        <Button
          dataTestId={TestIds.digitalSignage.repeatSlotCancelButton}
          type='button'
          variant={ButtonVariant.SECONDARY}
          handleOnClick={onClose}
          className='w-full'>
          {t('cancel')}
        </Button>
        <Button
          dataTestId={TestIds.digitalSignage.repeatSlotAddButton}
          type='button'
          variant={ButtonVariant.PRIMARY}
          disabled={!repeatEndDate}
          handleOnClick={() => repeatEndDate && onAdd(repeatEndDate)}
          className='w-full'>
          {t('add')}
        </Button>
      </div>
    </div>
  );
};

export default RepeatSlotModal;
